import { Stack, Table, TableData, Text } from '@mantine/core'
import { ContextModalProps } from '@mantine/modals'
import { useQuery } from '@tanstack/react-query'
import Loader from 'common/Loader'
import dayjs from 'dayjs'
import { shortagesQuery } from 'hooks/shortage'
import { ShortageOrderTableItem } from 'types/shortage'
import { getCategoryName, getShortageOrderStatusText, getUnitName } from 'utils'

type Props = {
  shortageOrder: ShortageOrderTableItem
}

export default function ShortagesModal({ innerProps: { shortageOrder } }: ContextModalProps<Props>) {
  const {
    data: shortages,
    isPending,
    isError,
    error,
  } = useQuery({
    ...shortagesQuery(undefined, {
      pagination: { pageIndex: 0, pageSize: 1000 },
      columnFilters: [{ id: 'shortageOrderId', value: shortageOrder.id }],
    }),
    staleTime: 0,
  })

  if (isPending) return <Loader />
  if (isError) return <div>Wystąpił błąd: {error.message}</div>

  const tableData: TableData = {
    head: ['Numer zamówienia', 'Ilość', 'Data dodania'],
    body: shortages.data.map((shortage) => [
      shortage.orderNumber,
      `${formatNumber(shortage.amount)} ${getUnitName(shortage.unit).short}`,
      dayjs(shortage.createdAt).format('D MMMM HH:mm'),
    ]),
  }

  return (
    <Stack>
      <Table variant="vertical" layout="fixed">
        <Table.Tbody>
          <Table.Tr>
            <Table.Th w={200}>Symbol produktu</Table.Th>
            <Table.Td>{shortageOrder.product.symbol}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>Wymiar</Table.Th>
            <Table.Td>{shortageOrder.size}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>Rodzaj produktu</Table.Th>
            <Table.Td>{getCategoryName(shortageOrder.category, { capitalize: true }).mianownik}</Table.Td>
          </Table.Tr>
          {shortageOrder.feature && (
            <Table.Tr>
              <Table.Th>Cecha</Table.Th>
              <Table.Td>{shortageOrder.feature}</Table.Td>
            </Table.Tr>
          )}
          {shortageOrder.shortageAmount > 0 && (
            <Table.Tr>
              <Table.Th>Łączna brakująca ilość</Table.Th>
              <Table.Td>
                {formatNumber(shortageOrder.shortageAmount)} {getUnitName(shortageOrder.unit).short}
              </Table.Td>
            </Table.Tr>
          )}
          <Table.Tr>
            <Table.Th>Zamówiona ilość</Table.Th>
            <Table.Td>
              {formatNumber(shortageOrder.orderedAmount)} {getUnitName(shortageOrder.unit).short}
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>Status</Table.Th>
            <Table.Td>{getShortageOrderStatusText(shortageOrder, true)}</Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
      {shortages.total > 0 ? (
        <Table.ScrollContainer minWidth={500} maxHeight={300}>
          <Table highlightOnHover data={tableData} />
        </Table.ScrollContainer>
      ) : (
        <Text ta="center" size="sm" fw="bold" py="md">
          Brak powiązanych braków
        </Text>
      )}
    </Stack>
  )
}

function formatNumber(num: number): string {
  const str = num.toString()
  return str.includes('.') ? str : `${str}.0`
}
